import type { ReactNode } from "react";
import { isHosted } from "../platform/telegram";
import { useNav, type Tab } from "./router";
import "./shell.css";

/** The browser rail mirrors the iOS tab set (including Inbox) so the preview
 * shows the app's real shape; inside Telegram the rail stays four-wide. Gated
 * tabs still render — as the full-app gate. */
const TABS: { id: Tab; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "connect", label: "Connect" },
  { id: "study", label: "Study" },
  ...(isHosted ? [] : [{ id: "inbox" as Tab, label: "Inbox" }]),
  { id: "profile", label: "Profile" },
];

/**
 * The top rail — Telegram build only.
 *
 * Deliberately not a bottom tab bar: Telegram has no such convention, and the
 * bottom of the viewport belongs to MainButton and the keyboard. Sitting the
 * rail under Telegram's own header keeps the contested edge clear and puts the
 * brand mark where the user already looks.
 *
 * The rail hides once a screen is pushed — that context belongs to the pushed
 * screen, and Telegram's BackButton is how you leave it.
 */
export function TabRail() {
  const { tab, setTab, stack } = useNav();
  if (stack.length > 0) return null;

  return (
    <nav className="rail">
      <span className="rail__mark">
        <img src="/brand/f-white-bold.png" alt="Freshman" />
      </span>
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`rail__item${t.id === tab ? " rail__item--active" : ""}`}
          onClick={() => setTab(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

/** Font Awesome stand-ins for the iOS tab symbols (house.fill, hand.wave.fill,
 * book.fill, bubble.left.and.bubble.right.fill, person.fill). */
const TAB_ICONS: Record<Tab, string> = {
  home: "fa-house",
  connect: "fa-hand",
  study: "fa-book",
  inbox: "fa-comments",
  profile: "fa-user",
};

/**
 * The bottom tab bar — browser build only, mirroring iOS MainTabView: five
 * tabs, icon over label, brand blue on the active tab (location/identity duty,
 * same argument as the rail's active tint — it is not a call to action).
 *
 * Always visible, even under a pushed screen: the browser has no Telegram
 * BackButton, so tapping any tab (including the current one) is the escape
 * hatch — setTab clears the pushed stack.
 */
export function BottomTabBar() {
  const { tab, setTab } = useNav();

  return (
    <nav className="tabbar">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`tabbar__item${t.id === tab ? " tabbar__item--active" : ""}`}
          onClick={() => setTab(t.id)}
        >
          <i className={`fa-solid ${TAB_ICONS[t.id]}`} aria-hidden="true" />
          {t.label}
        </button>
      ))}
    </nav>
  );
}

export function Screen({ children, padded = true }: { children: ReactNode; padded?: boolean }) {
  return <div className={`screen${padded ? " screen--padded" : ""}`}>{children}</div>;
}
